import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useIsBedrijfsadmin } from "../lib/permissions";
import { Modal, Field } from "./AdminCompaniesPage";
import BrainTypeSelect from "../components/BrainTypeSelect";
import { Plus, Trash, PencilSimple, UsersThree, ArrowRight } from "@phosphor-icons/react";

export default function JobCoachDashboardPage() {
  const { user } = useAuth();
  const isBedrijfsadmin = useIsBedrijfsadmin();
  const [coachees, setCoachees] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [brainType, setBrainType] = useState("");
  const [coachId, setCoachId] = useState("");
  const [filterCoach, setFilterCoach] = useState("");
  const [error, setError] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const promises = [api.get("/coachees")];
      if (isBedrijfsadmin) {
        promises.push(api.get("/job-coaches"));
      }
      const results = await Promise.all(promises);
      setCoachees(results[0].data);
      if (isBedrijfsadmin) setCoaches(results[1].data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [isBedrijfsadmin]);

  const openCreate = () => {
    setEditing(null); setName(""); setEmail(""); setPassword(""); setBrainType("");
    setCoachId(isBedrijfsadmin ? (user?.id || "") : "");
    setError(""); setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c); setName(c.name); setEmail(c.email); setPassword("");
    setBrainType(c.brain_type || "");
    setCoachId(c.job_coach_id || "");
    setError(""); setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      if (editing) {
        const payload = { name, email, brain_type: brainType };
        if (isBedrijfsadmin && coachId) payload.job_coach_id = coachId;
        await api.put(`/coachees/${editing.id}`, payload);
      } else {
        const payload = { name, email, password, brain_type: brainType };
        if (isBedrijfsadmin && coachId) payload.job_coach_id = coachId;
        await api.post("/coachees", payload);
      }
      setShowForm(false); fetchAll();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Coachee ${c.name} verwijderen?`)) return;
    await api.delete(`/coachees/${c.id}`);
    fetchAll();
  };

  const visible = isBedrijfsadmin && filterCoach
    ? coachees.filter((c) => c.job_coach_id === filterCoach)
    : coachees;

  const coachName = (id) => coaches.find((c) => c.id === id)?.name || "—";

  return (
    <AppLayout title={isBedrijfsadmin ? "Coachees van het bedrijf" : "Mijn coachees"} breadcrumbs={[{ label: "Dashboard" }]}>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="text-sm text-zinc-500">{visible.length} coachee{visible.length !== 1 ? "s" : ""}</div>
        <div className="flex items-center gap-3">
          {isBedrijfsadmin && coaches.length > 1 && (
            <select
              value={filterCoach}
              onChange={(e) => setFilterCoach(e.target.value)}
              data-testid="coachee-filter-coach"
              className="border border-zinc-300 px-3 py-2 text-sm rounded-sm bg-white"
            >
              <option value="">Alle work coaches</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.is_bedrijfsadmin ? " (bedrijfsadmin)" : ""}</option>
              ))}
            </select>
          )}
          <button onClick={openCreate} data-testid="add-coachee-button" className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
            <Plus size={16} /> Coachee toevoegen
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : visible.length === 0 ? (
        <div className="neuro-card p-12 text-center" data-testid="coachees-empty">
          <UsersThree size={32} className="mx-auto text-zinc-300" />
          <h3 className="font-display text-lg font-bold mt-3 tracking-tight">Nog geen coachees</h3>
          <p className="text-sm text-zinc-500 mt-1">Voeg je eerste coachee toe om aan de slag te gaan.</p>
        </div>
      ) : (
        <div className="neuro-card overflow-hidden" data-testid="coachees-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <Th>Naam</Th>
                <Th>E-mail</Th>
                {isBedrijfsadmin && <Th>Work coach</Th>}
                <Th>Breintype</Th>
                <Th>Test status</Th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-zinc-500">Acties</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`coachee-row-${c.id}`}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td>{c.email}</Td>
                  {isBedrijfsadmin && <Td className="text-zinc-600">{coachName(c.job_coach_id)}</Td>}
                  <Td className="font-mono text-xs">{c.brain_type || <span className="text-zinc-400">—</span>}</Td>
                  <Td>
                    {c.brain_type ? (
                      <span data-testid={`coachee-status-${c.id}`} className="inline-block bg-[#43A047]/10 text-[#43A047] border border-[#43A047]/30 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                        Definitief bepaald
                      </span>
                    ) : c.survey_completed_at ? (
                      <span data-testid={`coachee-status-${c.id}`} className="inline-block bg-[#FB8C00]/10 text-[#E65100] border border-[#FB8C00]/30 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                        Te valideren
                      </span>
                    ) : c.survey_enabled !== false ? (
                      <span data-testid={`coachee-status-${c.id}`} className="inline-block bg-[#1E88E5]/10 text-[#1E88E5] border border-[#1E88E5]/30 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                        Test open
                      </span>
                    ) : (
                      <span data-testid={`coachee-status-${c.id}`} className="inline-block bg-zinc-100 text-zinc-500 border border-zinc-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded-sm">
                        Geen test
                      </span>
                    )}
                  </Td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link to={`/coachees/${c.id}`} data-testid={`open-coachee-${c.id}`} className="inline-flex items-center gap-1 text-zinc-900 hover:text-[#1E88E5] mr-3 font-medium">
                      Open <ArrowRight size={14} />
                    </Link>
                    <button onClick={() => openEdit(c)} data-testid={`edit-coachee-${c.id}`} className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-900 mr-3">
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => onDelete(c)} data-testid={`delete-coachee-${c.id}`} className="inline-flex items-center gap-1 text-[#E53935] hover:text-[#E53935]/80">
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Modal title={editing ? "Coachee bewerken" : "Nieuwe coachee"} onClose={() => setShowForm(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="coachee-form">
            <Field label="Naam" value={name} onChange={setName} testid="coachee-name-input" />
            <Field label="E-mailadres" value={email} onChange={setEmail} type="email" testid="coachee-email-input" />
            {!editing && (
              <Field
                label="Wachtwoord (coachee kan hiermee inloggen)"
                type="password"
                value={password}
                onChange={setPassword}
                testid="coachee-password-input"
              />
            )}

            {isBedrijfsadmin && (
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Toegewezen work coach</label>
                <select
                  value={coachId}
                  onChange={(e) => setCoachId(e.target.value)}
                  data-testid="coachee-job-coach-input"
                  className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white"
                >
                  <option value="">— Selecteer work coach —</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.is_bedrijfsadmin ? " (bedrijfsadmin)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <BrainTypeSelect value={brainType} onChange={setBrainType} testid="coachee-brain-type-input" label="Breintype" />
            <p className="text-[11px] text-zinc-500 -mt-2">
              {editing
                ? "Wijzigingen aan het breintype overschrijven het huidige resultaat."
                : "Laat leeg om de coachee online de breintype-test te laten maken. Vul direct in als het breintype al bekend is — er wordt dan geen test beschikbaar."}
            </p>

            {error && <div className="text-sm text-[#E53935]" data-testid="coachee-form-error">{error}</div>}
            <button type="submit" data-testid="coachee-form-submit" className="w-full bg-zinc-900 text-white py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
              {editing ? "Opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}
    </AppLayout>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
